// © Microsoft Corporation. All rights reserved.

import { BrowserConsoleAppender } from "clientcore-infrastructure-analytics/appenders/BrowserConsoleAppender";
import { ILoggingService } from "clientcore-infrastructure-analytics/ILoggingService";
import { LogLevel } from "clientcore-infrastructure-analytics/models/LogLevel";
import { LoggingService } from "clientcore-infrastructure-analytics/LoggingService";
import { NodeConsoleAppender } from "clientcore-infrastructure-analytics/appenders/NodeConsoleAppender";
import { RuntimeEnvironment } from "clientcore-infrastructure-analytics/RuntimeEnvironment";
import { canUseDOM } from "@msnews/isomorphic-util";

/** The Logger class */
export class Logger {
    /** A default instance of the Logger class. */
    public static readonly defaultInstance: Logger = new Logger();

    /** Backing field for the isDebug function */
    private isDebugEnabled: boolean;

    /** The common logger for the application. The logging level for this is updated in initialize */
    private logger: ILoggingService;

    /** Backing field to hold reference to the window object */
    private windowObj?: Window;

    /**
     * The constructor
     */
    private constructor() {
        if (canUseDOM()) {
            this.setupBrowserEnvironment(window);

            if (this.isDebugEnabled) {
                // @ts-ignore
                window.webpackRequire = __webpack_require__;
            }
        } else {
            this.setupNodeEnvironment();
        }
    }

    /**
     * Setup client side logging environment - this sets / overrides the Window obj
     * @param windowObj - The Window obj to use
     */
    public setupBrowserEnvironment(windowObj: Window): void {
        this.logger = LoggingService.getInstance("ExperienceLogger", LogLevel.Info, RuntimeEnvironment.Browser);

        this.windowObj = windowObj;

        // Once we have a WindowObj, we can determine if debug mode is enabled
        this.isDebugEnabled = ((this.getQueryParameterByName(windowObj.location.href, "debug") || "").toLowerCase() in { 1: 1, true: 1 });

        this.setupLogLevel();
    }

    /**
     * Setup server side logging environment
     */
    public setupNodeEnvironment(): void {
        this.logger = LoggingService.getInstance("ExperienceLogger", LogLevel.Info, RuntimeEnvironment.NodeJs);
        this.isDebugEnabled = false; // isDebugEnabled should be set explictly using the setDebug method
        this.setupLogLevel();
    }

    /**
     * Returns the logging service
     * @returns {ILoggingService}
     */
    public getLoggingService(): ILoggingService {
        return this.logger;
    }

    /**
     * Checks whether we're running in debug mode.
     * @returns True when either debug=1 or debug=true is present as a query param, otherwise false.
     */
    public isDebug(): boolean {
        return this.isDebugEnabled;
    }

    /**
     * Override the isDebug setting. Useful when server side rendering.
     */
    public setDebug(isDebug: boolean): void {
        this.isDebugEnabled = isDebug;
        this.setupLogLevel();
    }

    /**
     * Log console message.
     * Note that these are not output to the console unless you're in debug mode.
     * @param message - Message to log.
     */
    public log(message: string): void {
        if (this.isDebug()) {
            if (!canUseDOM()) {
                // currently the logger is incredibly verbose when printing on the server side.
                // For now just directly print to console.
                // Work Item to address this: http://osgvsowi/24861651
                console.log("logger.info:", message);
            } else {
                this.logger.info(message);
            }
        }
    }

    /**
     * Log error message.
     * @param message - Message to log
     */
    public logError(message: string): void {
        this.logger.error(message);
    }

    /**
     * Lazy log console message. Use this when the message being constructed is complex or expensive so that it's only evaluated if needed.
     * Note that the callback is not invoked and the message is not output unless you're in debug mode.
     * @param getMessage - The function to call to get message to log
     */
    public logCallback(getMessage: () => string): void {
        this.logger.infoCallback(getMessage);
    }

    /**
     * Log the object to the console.
     * Note that these are not output to the console unless you're in debug mode.
     * @param message - Array of messages to log
     */
    public logObjects(...message: any[]): void {
        if (this.isDebug()) {
            if (canUseDOM() && this.windowObj) {
                this.windowObj.console.log(...message);
            } else {
                console.log("logger.logObjects:", ...message);
            }
        }
    }

    /**
     * Set logger log level.
     */
    private setupLogLevel(): void {
        // Note that we need to set the level at the root logger level or else the callback versions of the logging
        // methods will still be invoked to disseminate the message to the appenders.
        this.logger.setLevel(this.isDebug() ? LogLevel.Info : LogLevel.Error);
        this.setupAppenders();
    }

    /**
     * Add relevant appenders to the logger depending.
     */
    private setupAppenders(): void {
        this.logger.removeAllAppenders();
        if (canUseDOM()) {
            this.logger.addAppender(new BrowserConsoleAppender(this.logger.getLevel()));
        } else {
            this.logger.addAppender(new NodeConsoleAppender(this.logger.getLevel()));
        }
    }

    /**
     * Utility method to extract query string param in url.
     * @param name - Query param name
     * @returns Query param value
     */
    private getQueryParameterByName(locationHref: string, name: string): string {

        const nameRegEx = name.replace(/[[\]]/g, "\\$&");
        const regex: any = new RegExp("[?&]" + nameRegEx + "(=([^&#]*)|&|#|$)");

        const queryNameValueMatches: any = regex.exec(locationHref);
        if (!queryNameValueMatches) {
            return null;
        }

        // the second group would contain the value of the param
        const queryValue = queryNameValueMatches[2];

        return decodeURIComponent(queryValue.replace(/\+/g, " ")) || "";
    }
}

export const logger = Logger.defaultInstance;